import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';

// ============================================
// SHARED COMPONENTS
// ============================================

const Header = ({ title, subtitle, onBack, onSettings, rightContent = null }) => {
  const [time, setTime] = useState(new Date());
  
  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  return (
    <header className="px-6 py-4 flex items-center justify-between bg-white/80 backdrop-blur-sm border-b border-gray-200/60 sticky top-0 z-20">
      <div className="flex items-center gap-3">
        {onBack && (
          <button onClick={onBack} className="p-2 rounded-xl hover:bg-gray-100 transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-600">
              <path d="M15 19l-7-7 7-7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        )}
        <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/25">
          <svg viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" className="w-6 h-6">
            <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">{title}</h1>
          <p className="text-xs text-gray-400 font-medium">{subtitle}</p>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="text-right hidden sm:block">
          <div className="text-sm font-medium text-gray-700">Birmingham Central</div>
          <div className="text-xs text-gray-400 font-mono">{time.toLocaleTimeString('en-GB')}</div>
        </div>
        <div className="flex items-center gap-2">
          {onSettings && (
            <button onClick={onSettings} className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5 text-gray-500">
                <path d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          )}
          <button className="p-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5 text-gray-600">
              <path d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-red-500 rounded-full"/>
          </button>
          {rightContent}
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center text-sm font-bold text-white shadow-lg shadow-orange-500/25">
            AS
          </div>
        </div>
      </div>
    </header>
  );
};

// ============================================
// GOODS-IN HOME/DASHBOARD PAGE
// ============================================
const GoodsInHomePage = ({ onNavigate, onBack, onSettings }) => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => setLoaded(true), []);

  const menuItems = [
    { id: 'new-delivery', name: 'New Delivery', description: 'Scan invoices & verify stock', icon: 'plus', color: '#059669', count: null },
    { id: 'deliveries', name: 'All Deliveries', description: 'View & manage deliveries', icon: 'list', color: '#0891b2', count: '156' },
    { id: 'claims', name: 'Credit Claims', description: 'Manage discrepancy claims', icon: 'file', color: '#7c3aed', count: '12' },
    { id: 'batch', name: 'Batch Upload', description: 'Process multiple invoices', icon: 'upload', color: '#ea580c', count: null },
    { id: 'analytics', name: 'Analytics', description: 'Extraction performance', icon: 'chart', color: '#0284c7', count: null },
    { id: 'reconciliation', name: 'Reconciliation', description: 'Match invoices to orders', icon: 'check', color: '#16a34a', count: '3' },
  ];

  const icons = {
    plus: <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>,
    list: <path d="M4 6h16M4 12h16M4 18h16" strokeLinecap="round" strokeLinejoin="round"/>,
    file: <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>,
    upload: <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>,
    chart: <path d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" strokeLinecap="round" strokeLinejoin="round"/>,
    check: <path d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" strokeLinecap="round" strokeLinejoin="round"/>,
  };

  const stats = [
    { label: 'Today', value: '12', sublabel: 'Deliveries' },
    { label: 'Pending', value: '3', sublabel: 'Verification' },
    { label: 'Claims', value: '£1,245', sublabel: 'This month' },
    { label: 'Accuracy', value: '98.5%', sublabel: 'AI extraction' },
  ];

  const recentDeliveries = [
    { id: 1, ref: 'INV-2024-001234', wholesaler: 'AAH Healthcare', items: 24, status: 'verified', time: '10 min ago' },
    { id: 2, ref: 'INV-2024-001233', wholesaler: 'Alliance', items: 18, status: 'pending', time: '25 min ago' },
    { id: 3, ref: 'INV-2024-001232', wholesaler: 'Phoenix', items: 31, status: 'discrepancy', time: '1 hr ago' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Goods-In" subtitle="Delivery Verification" onBack={onBack} onSettings={onSettings}/>
      
      <main className="px-6 py-8 max-w-5xl mx-auto">
        {/* Stats Bar */}
        <div className={`grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8 transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
          {stats.map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
              <div className="text-xs text-gray-400">{stat.sublabel}</div>
            </div>
          ))}
        </div>

        {/* Menu Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
          {menuItems.map((item, index) => (
            <button
              key={item.id}
              onClick={() => onNavigate(item.id)}
              className={`group bg-white rounded-2xl border border-gray-200 p-5 text-left hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
              style={{ transitionDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between mb-3">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: item.color + '15' }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke={item.color} strokeWidth="2" className="w-6 h-6">
                    {icons[item.icon]}
                  </svg>
                </div>
                {item.count && (
                  <span className="px-2.5 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: item.color + '15', color: item.color }}>
                    {item.count}
                  </span>
                )}
              </div>
              <h3 className="font-semibold text-gray-900 mb-1">{item.name}</h3>
              <p className="text-sm text-gray-500">{item.description}</p>
            </button>
          ))}
        </div>

        {/* Recent Deliveries */}
        <div className={`bg-white rounded-2xl border border-gray-200 overflow-hidden transition-all duration-500 ${loaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: '300ms' }}>
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h3 className="font-bold text-gray-900">Recent Deliveries</h3>
            <button onClick={() => onNavigate('deliveries')} className="text-sm font-medium text-emerald-600 hover:text-emerald-700">
              View all →
            </button>
          </div>
          <div className="divide-y divide-gray-100">
            {recentDeliveries.map(delivery => (
              <div key={delivery.id} onClick={() => onNavigate('delivery-detail')} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50 cursor-pointer transition-colors">
                <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold text-xs">
                  {delivery.wholesaler.slice(0, 3).toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{delivery.ref}</div>
                  <div className="text-sm text-gray-500">{delivery.wholesaler} • {delivery.items} items</div>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                    delivery.status === 'verified' ? 'bg-emerald-100 text-emerald-700' :
                    delivery.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                    'bg-red-100 text-red-700'
                  }`}>
                    {delivery.status}
                  </span>
                  <div className="text-xs text-gray-400 mt-1">{delivery.time}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

// ============================================
// DELIVERIES LIST PAGE
// ============================================
const DeliveriesListPage = ({ onNavigate, onBack }) => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const deliveries = [
    { id: 1, ref: 'INV-2024-001234', wholesaler: 'AAH Healthcare', wholesalerCode: 'AAH', date: '2024-01-05', items: 24, status: 'verified' },
    { id: 2, ref: 'INV-2024-001233', wholesaler: 'Alliance Healthcare', wholesalerCode: 'ALL', date: '2024-01-05', items: 18, status: 'in_progress' },
    { id: 3, ref: 'INV-2024-001232', wholesaler: 'Phoenix Healthcare', wholesalerCode: 'PHX', date: '2024-01-05', items: 31, status: 'completed' },
    { id: 4, ref: 'INV-2024-001231', wholesaler: 'Lexon UK', wholesalerCode: 'LEX', date: '2024-01-04', items: 12, status: 'draft' },
    { id: 5, ref: 'INV-2024-001230', wholesaler: 'AAH Healthcare', wholesalerCode: 'AAH', date: '2024-01-04', items: 45, status: 'completed' },
  ];

  const statusConfig = {
    draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
    in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
    verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700' },
    completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Deliveries" subtitle="All delivery records" onBack={onBack}/>
      
      <main className="px-6 py-8 max-w-5xl mx-auto">
        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
              <path d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <input
              type="text"
              placeholder="Search by invoice ref..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <select value={filter} onChange={(e) => setFilter(e.target.value)} className="px-4 py-3 bg-white border border-gray-200 rounded-xl">
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="in_progress">In Progress</option>
            <option value="verified">Verified</option>
            <option value="completed">Completed</option>
          </select>
          <button onClick={() => onNavigate('new-delivery')} className="flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
              <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            New Delivery
          </button>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Invoice Ref</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Wholesaler</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Date</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Items</th>
                <th className="text-left px-6 py-4 text-sm font-semibold text-gray-600">Status</th>
                <th className="text-right px-6 py-4 text-sm font-semibold text-gray-600"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {deliveries.map(d => (
                <tr key={d.id} onClick={() => onNavigate('delivery-detail')} className="hover:bg-gray-50 cursor-pointer transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-xs">
                        {d.wholesalerCode}
                      </div>
                      <span className="font-medium text-gray-900">{d.ref}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{d.wholesaler}</td>
                  <td className="px-6 py-4 text-gray-600">{new Date(d.date).toLocaleDateString('en-GB')}</td>
                  <td className="px-6 py-4 text-gray-600">{d.items} items</td>
                  <td className="px-6 py-4">
                    <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-medium ${statusConfig[d.status].color}`}>
                      {statusConfig[d.status].label}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-400 inline">
                      <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </div>
  );
};

// ============================================
// CLAIMS PAGE
// ============================================
const ClaimsPage = ({ onNavigate, onBack }) => {
  const [filter, setFilter] = useState('all');

  const stats = [
    { label: 'Total Claims', value: '45', color: 'bg-blue-100 text-blue-700' },
    { label: 'Claimed Value', value: '£3,245', color: 'bg-amber-100 text-amber-700' },
    { label: 'Credits Received', value: '£2,890', color: 'bg-emerald-100 text-emerald-700' },
    { label: 'Pending', value: '8', color: 'bg-purple-100 text-purple-700' },
  ];

  const claims = [
    { id: 1, ref: 'CLM-2024-0045', wholesaler: 'AAH Healthcare', invoice: 'INV-2024-001232', value: 145.50, items: 3, status: 'submitted', dueDate: '2024-01-12' },
    { id: 2, ref: 'CLM-2024-0044', wholesaler: 'Alliance', invoice: 'INV-2024-001228', value: 89.20, items: 2, status: 'acknowledged', dueDate: '2024-01-10' },
    { id: 3, ref: 'CLM-2024-0043', wholesaler: 'Phoenix', invoice: 'INV-2024-001225', value: 234.00, items: 5, status: 'resolved', credit: 234.00 },
    { id: 4, ref: 'CLM-2024-0042', wholesaler: 'Lexon', invoice: 'INV-2024-001220', value: 56.80, items: 1, status: 'disputed' },
  ];

  const statusConfig = {
    draft: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
    submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-700' },
    acknowledged: { label: 'Acknowledged', color: 'bg-amber-100 text-amber-700' },
    resolved: { label: 'Resolved', color: 'bg-emerald-100 text-emerald-700' },
    disputed: { label: 'Disputed', color: 'bg-red-100 text-red-700' },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Credit Claims" subtitle="Manage discrepancy claims" onBack={onBack}/>
      
      <main className="px-6 py-8 max-w-5xl mx-auto">
        {/* Quick Links */}
        <div className="flex flex-wrap gap-3 mb-6">
          {['Overdue (2)', 'Templates', 'Approvals', 'Escalations'].map((link, i) => (
            <button key={i} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${i === 0 ? 'bg-red-100 text-red-700 hover:bg-red-200' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
              {link}
            </button>
          ))}
        </div>

        {/* Time Period Filter */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-gray-900">Summary</h2>
          <select className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700">
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month" selected>This Month</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {stats.map((stat, i) => (
            <div key={i} className={`rounded-2xl p-4 ${stat.color}`}>
              <div className="text-2xl font-bold">{stat.value}</div>
              <div className="text-sm opacity-80">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {['all', 'draft', 'submitted', 'acknowledged', 'resolved', 'disputed'].map(status => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${filter === status ? 'bg-emerald-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'}`}
            >
              {status === 'all' ? 'All Claims' : statusConfig[status]?.label || status}
            </button>
          ))}
        </div>

        {/* Claims List */}
        <div className="space-y-3">
          {claims.map(claim => (
            <div key={claim.id} onClick={() => onNavigate('claim-detail')} className="bg-white rounded-2xl border border-gray-200 p-5 hover:shadow-md cursor-pointer transition-all">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-3">
                  <span className="font-semibold text-gray-900">{claim.ref}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusConfig[claim.status].color}`}>
                    {statusConfig[claim.status].label}
                  </span>
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-400">
                  <path d="M9 5l7 7-7 7" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <p className="text-sm text-gray-500 mb-2">{claim.wholesaler} • {claim.invoice}</p>
              <div className="flex items-center gap-4 text-sm">
                <span className="font-medium text-gray-900">£{claim.value.toFixed(2)}</span>
                <span className="text-gray-500">{claim.items} item{claim.items !== 1 ? 's' : ''}</span>
                {claim.credit && <span className="text-emerald-600">Credit: £{claim.credit.toFixed(2)}</span>}
              </div>
              {claim.dueDate && claim.status === 'submitted' && (
                <div className="mt-3 pt-3 border-t border-gray-100 text-xs text-gray-500">
                  Due: {new Date(claim.dueDate).toLocaleDateString('en-GB')}
                </div>
              )}
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

// ============================================
// NEW DELIVERY PAGE
// ============================================
const NewDeliveryPage = ({ onBack, onComplete, onNavigate }) => {
  const [step, setStep] = useState('upload');
  const [invoiceImages, setInvoiceImages] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [lines, setLines] = useState([]);
  const [showMatched, setShowMatched] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const handleProcess = async () => {
    setStep('processing');
    setIsProcessing(true);
    for (let i = 0; i <= 100; i += 10) {
      await new Promise(r => setTimeout(r, 150));
      setProgress(i);
    }
    setLines([
      { id: '1', lineNumber: 1, invoiceProductName: 'Omeprazole 20mg Caps x28', invoicedQty: 5, receivedQty: 3, unitPrice: 12.50, status: 'mismatch' },
      { id: '2', lineNumber: 2, invoiceProductName: 'Amoxicillin 500mg Caps x21', invoicedQty: 3, receivedQty: 3, unitPrice: 8.75, status: 'matched' },
      { id: '3', lineNumber: 3, invoiceProductName: 'Metformin 500mg Tabs x56', invoicedQty: 2, receivedQty: 2, unitPrice: 4.20, status: 'matched' },
      { id: '4', lineNumber: 4, invoiceProductName: 'Paracetamol 500mg Tabs x100', invoicedQty: 10, receivedQty: 8, unitPrice: 3.50, status: 'mismatch' },
      { id: '5', lineNumber: 5, invoiceProductName: 'Ibuprofen 400mg Tabs x84', invoicedQty: 4, receivedQty: 4, unitPrice: 5.60, status: 'matched' },
      { id: '6', lineNumber: 6, invoiceProductName: 'Codeine 30mg Tabs x28', invoicedQty: 2, receivedQty: null, unitPrice: 15.00, status: 'pending' },
      { id: '7', lineNumber: 7, invoiceProductName: 'Tramadol 50mg Caps x30', invoicedQty: 1, receivedQty: null, unitPrice: 22.00, status: 'pending' },
    ]);
    setIsProcessing(false);
    setStep('review');
  };

  const handleSaveDelivery = () => {
    setShowConfirmModal(false);
    setStep('complete');
  };

  if (step === 'upload') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <Header title="New Delivery" subtitle="Upload & verify" onBack={onBack}/>
        <main className="px-6 py-8 max-w-3xl mx-auto">
          {/* Step indicator */}
          <div className="flex items-center justify-center gap-4 mb-8">
            {['Upload', 'Process', 'Review'].map((label, i) => (
              <React.Fragment key={label}>
                <div className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${i === 0 ? 'bg-emerald-600 text-white' : 'bg-gray-200 text-gray-500'}`}>{i + 1}</div>
                  <span className={`text-sm font-medium ${i === 0 ? 'text-gray-900' : 'text-gray-400'}`}>{label}</span>
                </div>
                {i < 2 && <div className="w-12 h-0.5 bg-gray-200"/>}
              </React.Fragment>
            ))}
          </div>

          {/* Upload card */}
          <div className="mb-6">
            <div className="bg-white rounded-2xl border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-bold text-gray-900">Invoice Photos</h3>
                <span className="text-sm text-gray-500">{invoiceImages.length} photos</span>
              </div>
              <label className="flex items-center justify-center gap-2 px-4 py-4 border-2 border-dashed border-blue-300 hover:bg-blue-50 rounded-xl cursor-pointer transition-colors">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-500">
                  <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span className="font-medium text-gray-700">Add Photos</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  files.forEach(file => {
                    const reader = new FileReader();
                    reader.onload = (ev) => {
                      const img = { id: Date.now() + Math.random(), preview: ev.target?.result };
                      setInvoiceImages(prev => [...prev, img]);
                    };
                    reader.readAsDataURL(file);
                  });
                }}/>
              </label>
            </div>
          </div>

          <button onClick={handleProcess} disabled={invoiceImages.length === 0} className="w-full px-6 py-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded-2xl font-semibold transition-colors">
            Process with AI
          </button>
        </main>
      </div>
    );
  }

  if (step === 'processing') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <div className="text-center">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6 relative">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-10 h-10 text-emerald-600">
              <path d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <div className="absolute inset-0 rounded-full border-4 border-emerald-600 border-t-transparent animate-spin"/>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Invoice</h2>
          <p className="text-gray-500 mb-6">Extracting line items with AI...</p>
          <div className="w-64 h-3 bg-gray-200 rounded-full mx-auto overflow-hidden">
            <div className="h-full bg-emerald-600 rounded-full transition-all" style={{ width: `${progress}%` }}/>
          </div>
          <p className="text-sm text-emerald-600 mt-3">{progress}%</p>
        </div>
      </div>
    );
  }

  // Completion screen
  if (step === 'complete') {
    const completedClaimItems = lines.filter(l => l.status === 'confirmed_incorrect');
    const completedClaimValue = completedClaimItems.reduce((sum, line) => {
      const shortQty = line.invoicedQty - (line.receivedQty || 0);
      return sum + (shortQty * (line.unitPrice || 0));
    }, 0);
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
        <div className="text-center max-w-md mx-auto px-6">
          <div className="w-24 h-24 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-6">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-12 h-12 text-emerald-600">
              <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Delivery Saved!</h2>
          <p className="text-gray-600 mb-8">
            {completedClaimItems.length > 0 
              ? `${completedClaimItems.length} claim${completedClaimItems.length > 1 ? 's' : ''} worth £${completedClaimValue.toFixed(2)} ${completedClaimItems.length > 1 ? 'have' : 'has'} been created.`
              : 'All items verified successfully with no discrepancies.'}
          </p>
          
          <div className="space-y-3">
            {completedClaimItems.length > 0 && (
              <button 
                onClick={() => onNavigate?.('claims')}
                className="w-full px-6 py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-semibold transition-colors flex items-center justify-center gap-2"
              >
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                View Credit Claims
              </button>
            )}
            <button 
              onClick={() => onComplete?.()}
              className={`w-full px-6 py-4 rounded-2xl font-semibold transition-colors ${
                completedClaimItems.length > 0 
                  ? 'bg-gray-100 hover:bg-gray-200 text-gray-700' 
                  : 'bg-emerald-600 hover:bg-emerald-700 text-white'
              }`}
            >
              Back to Goods-In
            </button>
            <button 
              onClick={() => {
                setStep('upload');
                setInvoiceImages([]);
                setLines([]);
                setShowMatched(false);
              }}
              className="w-full px-6 py-4 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-2xl font-semibold transition-colors"
            >
              Process Another Delivery
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Review step
  const matchedItems = lines.filter(l => l.status === 'matched');
  const mismatchedItems = lines.filter(l => l.status === 'mismatch');
  const pendingItems = lines.filter(l => l.status === 'pending');
  const confirmedIncorrectItems = lines.filter(l => l.status === 'confirmed_incorrect');
  
  const totalClaimValue = confirmedIncorrectItems.reduce((sum, line) => {
    const shortQty = line.invoicedQty - (line.receivedQty || 0);
    return sum + (shortQty * (line.unitPrice || 0));
  }, 0);

  const handleItemAction = (id, action) => {
    setLines(prev => prev.map(line => 
      line.id === id 
        ? { ...line, status: action === 'correct' ? 'matched' : 'confirmed_incorrect' }
        : line
    ));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Review Items" subtitle={`${lines.length} items extracted`} onBack={() => setStep('upload')}/>
      <main className="px-6 py-8 max-w-3xl mx-auto">
        {/* Action Buttons */}
        <div className="flex gap-3 mb-6">
          <button className="flex-1 flex flex-col items-center gap-2 bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md hover:border-emerald-300 transition-all">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-emerald-600">
                <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="12" cy="13" r="4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">Camera</span>
          </button>
          <button className="flex-1 flex flex-col items-center gap-2 bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 transition-all">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-blue-600">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">Upload</span>
          </button>
          <button className="flex-1 flex flex-col items-center gap-2 bg-white rounded-2xl border border-gray-200 p-4 hover:shadow-md hover:border-purple-300 transition-all">
            <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6 text-purple-600">
                <path d="M6 2v20M18 2v20M6 12h12M6 6h12M6 18h12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">Barcode</span>
          </button>
        </div>

        {/* Mismatched Items - Need Review */}
        {mismatchedItems.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-3 flex items-center gap-2">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4">
                <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Needs Review
            </h3>
            <div className="space-y-3">
              {mismatchedItems.map((line) => (
                <div key={line.id} className="bg-white rounded-2xl border-2 border-red-200 p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-medium text-gray-900">{line.invoiceProductName}</div>
                      <div className="text-sm text-gray-500 mt-1">
                        Invoice: <span className="font-semibold">{line.invoicedQty}</span> · 
                        Received: <span className="font-semibold text-red-600">{line.receivedQty}</span>
                      </div>
                    </div>
                    <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium">
                      Qty Mismatch
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => handleItemAction(line.id, 'incorrect')}
                      className="flex-1 px-4 py-2.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-xl text-sm font-semibold transition-colors"
                    >
                      ✗ Confirm Incorrect
                    </button>
                    <button 
                      onClick={() => handleItemAction(line.id, 'correct')}
                      className="flex-1 px-4 py-2.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-xl text-sm font-semibold transition-colors"
                    >
                      ✓ AI Error - Correct
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Pending Items - Not yet scanned */}
        {pendingItems.length > 0 && (
          <div className="mb-6">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
              Pending Verification ({pendingItems.length})
            </h3>
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="divide-y divide-gray-100">
                {pendingItems.map((line, index) => (
                  <div key={line.id} className="px-5 py-4 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center text-sm font-bold text-gray-400">
                        {line.lineNumber}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{line.invoiceProductName}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-bold text-gray-900">{line.invoicedQty}</div>
                      <div className="text-xs text-gray-400">qty</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Matched Items - Collapsible */}
        {matchedItems.length > 0 && (
          <div className="mb-6">
            <button 
              onClick={() => setShowMatched(!showMatched)}
              className="w-full bg-emerald-50 border border-emerald-200 rounded-2xl px-5 py-4 flex items-center justify-between hover:bg-emerald-100 transition-colors"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-emerald-600">
                    <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <div className="text-left">
                  <div className="font-semibold text-emerald-800">Verified Items</div>
                  <div className="text-sm text-emerald-600">{matchedItems.length} items confirmed correct</div>
                </div>
              </div>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className={`w-5 h-5 text-emerald-600 transition-transform ${showMatched ? 'rotate-180' : ''}`}>
                <path d="M19 9l-7 7-7-7" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            
            {showMatched && (
              <div className="mt-2 bg-white rounded-2xl border border-emerald-200 overflow-hidden">
                <div className="divide-y divide-gray-100">
                  {matchedItems.map((line) => (
                    <div key={line.id} className="px-5 py-3 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-emerald-500">
                          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                        <span className="text-gray-700">{line.invoiceProductName}</span>
                      </div>
                      <span className="font-medium text-gray-900">{line.invoicedQty}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Claims Summary - Shows items that will be claimed */}
        {confirmedIncorrectItems.length > 0 && (
          <div className="mb-6 bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl border border-red-200 p-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-red-600">
                  <path d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <div>
                <div className="font-bold text-gray-900">Claims Summary</div>
                <div className="text-sm text-gray-600">These items will be sent to Credit Claims</div>
              </div>
            </div>
            <div className="space-y-2 mb-4">
              {confirmedIncorrectItems.map((line) => {
                const shortQty = line.invoicedQty - (line.receivedQty || 0);
                const claimValue = shortQty * (line.unitPrice || 0);
                return (
                  <div key={line.id} className="bg-white rounded-xl p-3 flex items-center justify-between">
                    <div>
                      <div className="font-medium text-gray-900 text-sm">{line.invoiceProductName}</div>
                      <div className="text-xs text-gray-500">Short: {shortQty} units @ £{(line.unitPrice || 0).toFixed(2)}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-bold text-red-600">£{claimValue.toFixed(2)}</div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between pt-3 border-t border-red-200">
              <span className="font-semibold text-gray-700">Total Claim Value</span>
              <span className="text-xl font-bold text-red-600">£{totalClaimValue.toFixed(2)}</span>
            </div>
          </div>
        )}

        <button 
          onClick={() => confirmedIncorrectItems.length > 0 ? setShowConfirmModal(true) : handleSaveDelivery()} 
          disabled={mismatchedItems.length > 0 || pendingItems.length > 0}
          className="w-full px-6 py-4 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white rounded-2xl font-semibold transition-colors"
        >
          {mismatchedItems.length > 0 || pendingItems.length > 0 
            ? `Review ${mismatchedItems.length + pendingItems.length} Item${(mismatchedItems.length + pendingItems.length) > 1 ? 's' : ''} First` 
            : confirmedIncorrectItems.length > 0 
              ? `Save & Create ${confirmedIncorrectItems.length} Claim${confirmedIncorrectItems.length > 1 ? 's' : ''}`
              : 'Save Delivery'}
        </button>

        {/* Confirmation Modal */}
        {showConfirmModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-4">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8 text-amber-600">
                    <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Confirm Claims Submission</h3>
                <p className="text-gray-600">
                  <span className="font-semibold text-red-600">{confirmedIncorrectItems.length} item{confirmedIncorrectItems.length > 1 ? 's' : ''}</span> with a total value of <span className="font-semibold text-red-600">£{totalClaimValue.toFixed(2)}</span> will be sent to Credit Claims.
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-xl p-4 mb-6 max-h-40 overflow-y-auto">
                {confirmedIncorrectItems.map((line) => {
                  const shortQty = line.invoicedQty - (line.receivedQty || 0);
                  return (
                    <div key={line.id} className="flex justify-between text-sm py-1">
                      <span className="text-gray-700">{line.invoiceProductName}</span>
                      <span className="text-gray-500">-{shortQty}</span>
                    </div>
                  );
                })}
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setShowConfirmModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={handleSaveDelivery}
                  className="flex-1 px-4 py-3 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-semibold transition-colors"
                >
                  Confirm & Save
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ============================================
// ANALYTICS PAGE
// ============================================
const AnalyticsPage = ({ onBack }) => {
  const stats = [
    { label: 'Total Processed', value: '1,245', change: '+12%' },
    { label: 'Extraction Accuracy', value: '98.5%', change: '+0.3%' },
    { label: 'Avg Processing Time', value: '2.3s', change: '-0.5s' },
    { label: 'Auto-matched', value: '94.2%', change: '+1.1%' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Analytics" subtitle="Extraction performance" onBack={onBack}/>
      
      <main className="px-6 py-8 max-w-5xl mx-auto">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {stats.map((stat, i) => (
            <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-sm text-gray-500">{stat.label}</div>
              <div className={`text-xs mt-1 ${stat.change.startsWith('+') ? 'text-emerald-600' : 'text-blue-600'}`}>
                {stat.change} vs last month
              </div>
            </div>
          ))}
        </div>

        {/* Chart placeholder */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 mb-6">
          <h3 className="font-bold text-gray-900 mb-4">Extraction Volume</h3>
          <div className="h-64 flex items-center justify-center bg-gray-50 rounded-xl">
            <p className="text-gray-400">Chart visualization would go here</p>
          </div>
        </div>

        {/* By Wholesaler */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6">
          <h3 className="font-bold text-gray-900 mb-4">By Wholesaler</h3>
          {['AAH Healthcare', 'Alliance Healthcare', 'Phoenix Healthcare', 'Lexon UK'].map((w, i) => (
            <div key={w} className="flex items-center justify-between py-3 border-b border-gray-100 last:border-0">
              <span className="font-medium text-gray-900">{w}</span>
              <div className="flex items-center gap-4">
                <span className="text-gray-500">{[423, 312, 287, 223][i]} invoices</span>
                <span className="text-emerald-600 font-medium">{[99.1, 98.4, 97.8, 98.9][i]}%</span>
              </div>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
};

// ============================================
// BATCH UPLOAD PAGE
// ============================================
const BatchUploadPage = ({ onBack }) => {
  const [files, setFiles] = useState([]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Batch Upload" subtitle="Process multiple invoices" onBack={onBack}/>
      
      <main className="px-6 py-8 max-w-3xl mx-auto">
        <div className="bg-white rounded-2xl border border-gray-200 p-8">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full bg-orange-100 flex items-center justify-center mx-auto mb-4">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8 text-orange-600">
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Upload Multiple Invoices</h2>
            <p className="text-gray-500">Drop invoice images or PDFs to process them in batch</p>
          </div>

          <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center hover:border-orange-400 transition-colors cursor-pointer">
            <input type="file" multiple accept="image/*,.pdf" className="hidden" id="batch-upload"/>
            <label htmlFor="batch-upload" className="cursor-pointer">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-12 h-12 text-gray-400 mx-auto mb-4">
                <path d="M12 4v16m8-8H4" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="text-gray-600 font-medium">Click to select files or drag & drop</p>
              <p className="text-sm text-gray-400 mt-1">Supports JPG, PNG, PDF up to 50 files</p>
            </label>
          </div>

          {files.length > 0 && (
            <div className="mt-6">
              <h3 className="font-medium text-gray-900 mb-3">{files.length} files selected</h3>
              <button className="w-full px-6 py-4 bg-orange-600 hover:bg-orange-700 text-white rounded-xl font-semibold transition-colors">
                Start Processing
              </button>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

// ============================================
// RECONCILIATION PAGE
// ============================================
const ReconciliationPage = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('reconcile');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showClaimModal, setShowClaimModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Invoices uploaded by office/branch (what we've been charged for)
  const uploadedInvoices = [
    { id: 1, ref: 'INV-2024-001240', wholesaler: 'AAH Healthcare', date: '2024-01-05', total: 1245.50, items: 24, status: 'matched', scannedRef: 'DEL-2024-0156' },
    { id: 2, ref: 'INV-2024-001239', wholesaler: 'Alliance Healthcare', date: '2024-01-05', total: 890.20, items: 18, status: 'matched', scannedRef: 'DEL-2024-0155' },
    { id: 3, ref: 'INV-2024-001238', wholesaler: 'Phoenix Healthcare', date: '2024-01-05', total: 456.00, items: 12, status: 'missing', scannedRef: null },
    { id: 4, ref: 'INV-2024-001237', wholesaler: 'AAH Healthcare', date: '2024-01-04', total: 678.30, items: 15, status: 'partial', scannedRef: 'DEL-2024-0154', shortage: 89.50 },
    { id: 5, ref: 'INV-2024-001236', wholesaler: 'Lexon UK', date: '2024-01-04', total: 234.00, items: 8, status: 'missing', scannedRef: null },
  ];

  // Scanned deliveries at branch (what actually arrived)
  const scannedDeliveries = [
    { id: 1, ref: 'DEL-2024-0156', wholesaler: 'AAH Healthcare', date: '2024-01-05', items: 24, status: 'verified', matchedInvoice: 'INV-2024-001240' },
    { id: 2, ref: 'DEL-2024-0155', wholesaler: 'Alliance Healthcare', date: '2024-01-05', items: 18, status: 'verified', matchedInvoice: 'INV-2024-001239' },
    { id: 3, ref: 'DEL-2024-0154', wholesaler: 'AAH Healthcare', date: '2024-01-04', items: 13, status: 'discrepancy', matchedInvoice: 'INV-2024-001237' },
    { id: 4, ref: 'DEL-2024-0153', wholesaler: 'Phoenix Healthcare', date: '2024-01-04', items: 22, status: 'verified', matchedInvoice: null },
  ];

  const matchedCount = uploadedInvoices.filter(i => i.status === 'matched').length;
  const missingCount = uploadedInvoices.filter(i => i.status === 'missing').length;
  const partialCount = uploadedInvoices.filter(i => i.status === 'partial').length;
  const missingValue = uploadedInvoices.filter(i => i.status === 'missing').reduce((sum, i) => sum + i.total, 0);
  const shortageValue = uploadedInvoices.filter(i => i.status === 'partial').reduce((sum, i) => sum + (i.shortage || 0), 0);

  const tabs = [
    { id: 'reconcile', name: 'Reconcile', count: missingCount + partialCount },
    { id: 'scanned', name: 'Scanned Deliveries', count: scannedDeliveries.filter(d => !d.matchedInvoice).length },
    { id: 'history', name: 'History', count: null },
  ];

  const handleClaimAll = (invoice) => {
    setSelectedInvoice(invoice);
    setShowClaimModal(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Reconciliation" subtitle="Match invoices to deliveries" onBack={onBack}/>
      
      <main className="px-6 py-8 max-w-5xl mx-auto">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-5 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                activeTab === tab.id 
                  ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-500/25' 
                  : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'
              }`}
            >
              {tab.name}
              {tab.count > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${
                  activeTab === tab.id ? 'bg-white/20 text-white' : 'bg-red-100 text-red-600'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>

        {activeTab === 'reconcile' && (
          <>
            {/* Time Period Filter */}
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900">Summary</h2>
              <select className="px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700">
                <option value="today">Today</option>
                <option value="week">This Week</option>
                <option value="month" selected>This Month</option>
                <option value="all">All Time</option>
              </select>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-emerald-100 text-emerald-700 rounded-2xl p-4">
                <div className="text-2xl font-bold">{matchedCount}</div>
                <div className="text-sm">Matched</div>
              </div>
              <div className="bg-red-100 text-red-700 rounded-2xl p-4">
                <div className="text-2xl font-bold">{missingCount}</div>
                <div className="text-sm">Missing Boxes</div>
              </div>
              <div className="bg-amber-100 text-amber-700 rounded-2xl p-4">
                <div className="text-2xl font-bold">{partialCount}</div>
                <div className="text-sm">Part Shortage</div>
              </div>
              <div className="bg-purple-100 text-purple-700 rounded-2xl p-4">
                <div className="text-2xl font-bold">£{(missingValue + shortageValue).toFixed(0)}</div>
                <div className="text-sm">To Claim</div>
              </div>
            </div>

            {/* Upload Invoices Button */}
            <button 
              onClick={() => setShowUploadModal(true)}
              className="w-full mb-6 px-6 py-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white rounded-2xl font-semibold transition-all shadow-lg shadow-blue-500/25 flex items-center justify-center gap-3"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-6 h-6">
                <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              Upload Invoices from Wholesaler
            </button>

            {/* Missing Boxes - Entire invoices not received */}
            {missingCount > 0 && (
              <div className="bg-white rounded-2xl border-2 border-red-200 overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-red-100 bg-red-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-red-600">
                        <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-red-800">Missing Boxes</h3>
                      <p className="text-sm text-red-600">Invoiced but no delivery scanned - box may be lost in transit</p>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {uploadedInvoices.filter(i => i.status === 'missing').map(invoice => (
                    <div key={invoice.id} className="px-6 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center text-red-700 font-bold text-xs">
                            {invoice.wholesaler.slice(0, 3).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{invoice.ref}</div>
                            <div className="text-sm text-gray-500">{invoice.wholesaler} • {invoice.items} items</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-red-600">£{invoice.total.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">{new Date(invoice.date).toLocaleDateString('en-GB')}</div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button className="flex-1 px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl text-sm font-semibold transition-colors">
                          Mark as Received
                        </button>
                        <button 
                          onClick={() => handleClaimAll(invoice)}
                          className="flex-1 px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-sm font-semibold transition-colors"
                        >
                          Claim Entire Box
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Partial Shortages - Some items missing from box */}
            {partialCount > 0 && (
              <div className="bg-white rounded-2xl border-2 border-amber-200 overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-amber-100 bg-amber-50">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-amber-600">
                        <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <div>
                      <h3 className="font-bold text-amber-800">Part Shortages</h3>
                      <p className="text-sm text-amber-600">Box received but items missing inside</p>
                    </div>
                  </div>
                </div>
                <div className="divide-y divide-gray-100">
                  {uploadedInvoices.filter(i => i.status === 'partial').map(invoice => (
                    <div key={invoice.id} className="px-6 py-4">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-xs">
                            {invoice.wholesaler.slice(0, 3).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-semibold text-gray-900">{invoice.ref}</div>
                            <div className="text-sm text-gray-500">{invoice.wholesaler}</div>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className="text-lg font-bold text-amber-600">£{invoice.shortage?.toFixed(2)}</div>
                          <div className="text-xs text-gray-500">shortage</div>
                        </div>
                      </div>
                      <button className="w-full px-4 py-2.5 bg-amber-100 hover:bg-amber-200 text-amber-700 rounded-xl text-sm font-semibold transition-colors">
                        View Claim
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Matched Invoices */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-gray-900">Matched Invoices</h3>
                  <p className="text-sm text-gray-500">Invoice received and delivery scanned - all good</p>
                </div>
                <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">
                  {matchedCount} matched
                </span>
              </div>
              <div className="divide-y divide-gray-100">
                {uploadedInvoices.filter(i => i.status === 'matched').map(invoice => (
                  <div key={invoice.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-emerald-600">
                          <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{invoice.ref}</div>
                        <div className="text-sm text-gray-500">{invoice.wholesaler} • {invoice.scannedRef}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium text-gray-900">£{invoice.total.toFixed(2)}</div>
                      <div className="text-xs text-gray-500">{new Date(invoice.date).toLocaleDateString('en-GB')}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'scanned' && (
          <>
            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 mb-6 flex items-start gap-3">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-blue-600 mt-0.5">
                <path d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <div>
                <p className="text-sm text-blue-800 font-medium">Deliveries scanned at branch</p>
                <p className="text-sm text-blue-600">These will be matched against invoices when uploaded by office</p>
              </div>
            </div>

            {/* Unmatched Scanned Deliveries */}
            {scannedDeliveries.filter(d => !d.matchedInvoice).length > 0 && (
              <div className="bg-white rounded-2xl border-2 border-blue-200 overflow-hidden mb-6">
                <div className="px-6 py-4 border-b border-blue-100 bg-blue-50">
                  <h3 className="font-bold text-blue-800">Awaiting Invoice Match</h3>
                  <p className="text-sm text-blue-600">Scanned deliveries not yet matched to uploaded invoices</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {scannedDeliveries.filter(d => !d.matchedInvoice).map(delivery => (
                    <div key={delivery.id} className="px-6 py-4 flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-blue-700 font-bold text-xs">
                          {delivery.wholesaler.slice(0, 3).toUpperCase()}
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900">{delivery.ref}</div>
                          <div className="text-sm text-gray-500">{delivery.wholesaler} • {delivery.items} items scanned</div>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${
                          delivery.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'
                        }`}>
                          {delivery.status === 'verified' ? 'Verified' : 'Has discrepancies'}
                        </span>
                        <div className="text-xs text-gray-500 mt-1">{new Date(delivery.date).toLocaleDateString('en-GB')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* All Scanned Deliveries */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-100">
                <h3 className="font-bold text-gray-900">All Scanned Deliveries</h3>
              </div>
              <div className="divide-y divide-gray-100">
                {scannedDeliveries.map(delivery => (
                  <div key={delivery.id} className="px-6 py-4 flex items-center justify-between hover:bg-gray-50">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                        delivery.matchedInvoice ? 'bg-emerald-100' : 'bg-gray-100'
                      }`}>
                        {delivery.matchedInvoice ? (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-emerald-600">
                            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        ) : (
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-400">
                            <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{delivery.ref}</div>
                        <div className="text-sm text-gray-500">
                          {delivery.wholesaler} • {delivery.items} items
                          {delivery.matchedInvoice && <span className="text-emerald-600"> • {delivery.matchedInvoice}</span>}
                        </div>
                      </div>
                    </div>
                    <div className="text-xs text-gray-500">{new Date(delivery.date).toLocaleDateString('en-GB')}</div>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === 'history' && (
          <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
            <div className="text-4xl mb-4">📋</div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Reconciliation History</h3>
            <p className="text-gray-500">View past reconciliations and claims</p>
          </div>
        )}

        {/* Upload Invoices Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-lg w-full p-6 shadow-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Upload Invoices</h3>
                <button onClick={() => setShowUploadModal(false)} className="p-2 hover:bg-gray-100 rounded-xl">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-gray-500">
                    <path d="M6 18L18 6M6 6l12 12" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6">
                <p className="text-sm text-blue-800">
                  Upload invoices charged to this branch. The system will automatically match them against scanned deliveries.
                </p>
              </div>

              {/* Wholesaler Filter */}
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Wholesaler (optional)</label>
                <select className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm">
                  <option value="">All wholesalers</option>
                  <option value="aah">AAH Healthcare</option>
                  <option value="alliance">Alliance Healthcare</option>
                  <option value="phoenix">Phoenix Healthcare</option>
                  <option value="lexon">Lexon UK</option>
                </select>
              </div>

              {/* Upload Area */}
              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Invoice Images/PDFs</label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-blue-400 transition-colors cursor-pointer">
                  <input type="file" accept="image/*,.pdf" multiple className="hidden" id="invoice-upload"/>
                  <label htmlFor="invoice-upload" className="cursor-pointer">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-10 h-10 text-gray-400 mx-auto mb-3">
                      <path d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    <p className="text-gray-600 font-medium">Click to upload or drag & drop</p>
                    <p className="text-sm text-gray-400 mt-1">Multiple files supported - JPG, PNG, PDF</p>
                  </label>
                </div>
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => setShowUploadModal(false)}
                  className="flex-1 px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-semibold transition-colors"
                >
                  Upload & Match
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Claim Entire Box Modal */}
        {showClaimModal && selectedInvoice && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-md w-full p-6 shadow-2xl">
              <div className="text-center mb-6">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-8 h-8 text-red-600">
                    <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Claim Missing Box</h3>
                <p className="text-gray-600">This will create a claim for the entire invoice as the box was never received.</p>
              </div>

              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Invoice</span>
                  <span className="font-semibold text-gray-900">{selectedInvoice.ref}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Wholesaler</span>
                  <span className="font-semibold text-gray-900">{selectedInvoice.wholesaler}</span>
                </div>
                <div className="flex justify-between items-center mb-3">
                  <span className="text-sm text-gray-500">Items</span>
                  <span className="font-semibold text-gray-900">{selectedInvoice.items} items</span>
                </div>
                <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                  <span className="text-sm text-gray-500">Claim Value</span>
                  <span className="font-bold text-red-600">£{selectedInvoice.total.toFixed(2)}</span>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-semibold text-gray-700 mb-2">Reason (optional)</label>
                <textarea 
                  placeholder="e.g. Box not delivered, driver confirmed not on van..."
                  className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm resize-none"
                  rows={3}
                />
              </div>

              <div className="flex gap-3">
                <button 
                  onClick={() => {
                    setShowClaimModal(false);
                    setSelectedInvoice(null);
                  }}
                  className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold transition-colors"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    setShowClaimModal(false);
                    setSelectedInvoice(null);
                    // Would navigate to claims
                  }}
                  className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl font-semibold transition-colors"
                >
                  Create Claim
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

// ============================================
// SETTINGS PAGE
// ============================================
const SettingsPage = ({ onBack }) => {
  const [activeTab, setActiveTab] = useState('api');
  const [showApiKey, setShowApiKey] = useState({});

  const tabs = [
    { id: 'api', name: 'API Configuration' },
    { id: 'branches', name: 'Branches' },
    { id: 'suppliers', name: 'Suppliers' },
    { id: 'users', name: 'Users & Roles' },
    { id: 'cascade', name: 'Cascade Rules' },
    { id: 'notifications', name: 'Notifications' },
  ];

  const apis = [
    { id: 'claude', name: 'Claude AI', description: 'Invoice OCR & stock verification', status: 'connected', icon: '🤖' },
    { id: 'trud', name: 'NHS TRUD', description: 'Drug dictionary (dm+d)', status: 'connected', icon: '🏥' },
    { id: 'aah', name: 'AAH Healthcare', description: 'Ordering & pricing API', status: 'connected', icon: '💊' },
    { id: 'alliance', name: 'Alliance Healthcare', description: 'Ordering & pricing API', status: 'connected', icon: '💊' },
    { id: 'phoenix', name: 'Phoenix Healthcare', description: 'Ordering & pricing API', status: 'error', icon: '💊' },
    { id: 'lexon', name: 'Lexon UK', description: 'Daily price files', status: 'pending', icon: '💊' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' }}>
      <Header title="Settings" subtitle="System Configuration" onBack={onBack} rightContent={
        <button className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold rounded-xl ml-2">Save Changes</button>
      }/>
      
      <div className="flex max-w-6xl mx-auto">
        {/* Sidebar */}
        <aside className="w-56 p-4 border-r border-gray-200/60 min-h-[calc(100vh-73px)] bg-white/50">
          <nav className="space-y-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full px-4 py-3 rounded-xl text-left text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-indigo-50 text-indigo-700' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {tab.name}
              </button>
            ))}
          </nav>
        </aside>

        {/* Content */}
        <main className="flex-1 p-6">
          {activeTab === 'api' && (
            <div className="space-y-6">
              {/* Claude AI */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-2xl">🤖</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900">Claude AI Configuration</h3>
                    <p className="text-sm text-gray-500">Powers invoice OCR, stock verification, and ordering suggestions</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"/>
                    <span className="text-sm font-medium text-emerald-600">Connected</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">API Key</label>
                    <div className="flex gap-3">
                      <div className="flex-1 relative">
                        <input type={showApiKey.claude ? "text" : "password"} defaultValue="sk-ant-api03-xxxxxxxxxxxxxxxxxxxxxxxx" readOnly className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 font-mono text-sm"/>
                        <button onClick={() => setShowApiKey(prev => ({ ...prev, claude: !prev.claude }))} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                          👁
                        </button>
                      </div>
                      <button className="px-4 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700">Update</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Model</label>
                      <select className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm">
                        <option>claude-sonnet-4-20250514</option>
                        <option>claude-opus-4-20250514</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Max Tokens</label>
                      <input type="number" defaultValue="4096" className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm"/>
                    </div>
                  </div>
                </div>
              </div>

              {/* NHS TRUD API */}
              <div className="bg-white rounded-2xl border border-gray-200 p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center text-2xl">🏥</div>
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-900">NHS TRUD API</h3>
                    <p className="text-sm text-gray-500">Dictionary of Medicines and Devices (dm+d) - Drug names & codes</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"/>
                    <span className="text-sm font-medium text-emerald-600">Connected</span>
                  </div>
                </div>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">TRUD API Key</label>
                    <div className="flex gap-3">
                      <input type={showApiKey.trud ? "text" : "password"} defaultValue="trud-xxxxxxxxxxxxxxxxxxxx" readOnly className="flex-1 px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 font-mono text-sm"/>
                      <button onClick={() => setShowApiKey(prev => ({ ...prev, trud: !prev.trud }))} className="px-3 text-gray-400 hover:text-gray-600">👁</button>
                      <button className="px-4 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700">Update</button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Last Sync</label>
                      <div className="px-4 py-3 rounded-xl bg-gray-50 border border-gray-200 text-sm text-gray-600">
                        2 days ago • 87,432 products
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Auto-sync</label>
                      <select className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm">
                        <option>Weekly</option>
                        <option>Daily</option>
                        <option>Monthly</option>
                      </select>
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl text-sm font-semibold hover:bg-blue-200">
                    Sync Now
                  </button>
                </div>
              </div>

              {/* Supplier APIs */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h3 className="font-bold text-gray-900">Supplier & Wholesaler APIs</h3>
                  <p className="text-sm text-gray-500">Used for ordering, pricing, and stock checks</p>
                </div>
                <div className="divide-y divide-gray-100">
                  {apis.filter(a => !['claude', 'trud'].includes(a.id)).map(api => (
                    <div key={api.id} className="px-6 py-4 flex items-center gap-4 hover:bg-gray-50">
                      <div className="w-12 h-12 rounded-xl bg-gray-100 flex items-center justify-center text-2xl">{api.icon}</div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900">{api.name}</h4>
                        <p className="text-sm text-gray-500">{api.description}</p>
                      </div>
                      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${
                        api.status === 'connected' ? 'bg-emerald-50 text-emerald-700' :
                        api.status === 'error' ? 'bg-red-50 text-red-700' :
                        'bg-amber-50 text-amber-700'
                      }`}>
                        <span className={`w-2 h-2 rounded-full ${
                          api.status === 'connected' ? 'bg-emerald-500' :
                          api.status === 'error' ? 'bg-red-500' :
                          'bg-amber-500'
                        }`}/>
                        {api.status === 'connected' ? 'Connected' : api.status === 'error' ? 'Error' : 'Pending'}
                      </div>
                      <button className="px-4 py-2 rounded-xl bg-gray-100 hover:bg-gray-200 text-sm font-semibold text-gray-700">
                        Configure
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {activeTab !== 'api' && (
            <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center">
              <div className="text-4xl mb-4">🚧</div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{tabs.find(t => t.id === activeTab)?.name}</h3>
              <p className="text-gray-500">Configuration panel coming soon...</p>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// ============================================
// MAIN APP ROUTER
// ============================================
const GoodsInModule = () => {
  const [currentPage, setCurrentPage] = useState('home');
  const [showMobilePreview, setShowMobilePreview] = useState(true);

  const navigate = (page) => setCurrentPage(page);
  const goBack = () => setCurrentPage('home');
  const goSettings = () => setCurrentPage('settings');

  const pages = {
    'home': <GoodsInHomePage onNavigate={navigate} onBack={null} onSettings={goSettings}/>,
    'new-delivery': <NewDeliveryPage onBack={goBack} onComplete={goBack} onNavigate={navigate}/>,
    'deliveries': <DeliveriesListPage onNavigate={navigate} onBack={goBack}/>,
    'claims': <ClaimsPage onNavigate={navigate} onBack={goBack}/>,
    'batch': <BatchUploadPage onBack={goBack}/>,
    'analytics': <AnalyticsPage onBack={goBack}/>,
    'reconciliation': <ReconciliationPage onBack={goBack}/>,
    'settings': <SettingsPage onBack={goBack}/>,
  };

  const content = pages[currentPage] || pages['home'];

  // Mobile preview wrapper
  if (showMobilePreview) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        {/* Toggle Button */}
        <button 
          onClick={() => setShowMobilePreview(false)}
          className="fixed top-4 right-4 px-4 py-2 bg-white rounded-lg text-sm font-medium text-gray-700 shadow-lg hover:bg-gray-100 z-50"
        >
          View Desktop →
        </button>
        
        {/* Phone Frame */}
        <div className="relative">
          {/* Phone outer frame */}
          <div className="bg-gray-800 rounded-[3rem] p-3 shadow-2xl">
            {/* Phone inner bezel */}
            <div className="bg-black rounded-[2.5rem] p-2">
              {/* Dynamic Island / Notch */}
              <div className="absolute top-6 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full z-50"/>
              
              {/* Screen */}
              <div className="w-[375px] h-[812px] bg-white rounded-[2rem] overflow-hidden relative">
                {/* Status bar background */}
                <div className="absolute top-0 left-0 right-0 h-12 bg-white/80 backdrop-blur-sm z-40 flex items-center justify-between px-8">
                  <span className="text-sm font-semibold">9:41</span>
                  <div className="flex items-center gap-1">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M12 3C6.95 3 3 6.95 3 12s3.95 9 9 9c.35 0 .69-.02 1.03-.06-.1-.32-.17-.65-.2-.99C12.56 20 12.28 20 12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8c0 .28 0 .56-.05.83.35.03.68.1.99.2.04-.34.06-.68.06-1.03 0-4.95-3.95-9-9-9z"/>
                    </svg>
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                    </svg>
                    <div className="w-6 h-3 border border-current rounded-sm relative">
                      <div className="absolute inset-0.5 bg-current rounded-xs" style={{width: '80%'}}/>
                    </div>
                  </div>
                </div>
                
                {/* App Content */}
                <div className="h-full overflow-y-auto">
                  {content}
                </div>
                
                {/* Home indicator */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-32 h-1 bg-gray-800 rounded-full"/>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <button 
        onClick={() => setShowMobilePreview(true)}
        className="fixed top-4 right-4 px-4 py-2 bg-gray-800 rounded-lg text-sm font-medium text-white shadow-lg hover:bg-gray-700 z-50"
      >
        📱 View Mobile
      </button>
      {content}
    </div>
  );
};

export default GoodsInModule;
